import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { BackLink, DispensationExtended, FormInput } from '@sdir/blueprint.aps/lib/components';
import { AlertSingleLine, Loader, SdirButton } from '@sdir/sds';
// eslint-disable-next-line max-len
import { DispensationServiceFeaturesDispensationsSharedDispensationDto as DispensationDto } from '@sdir/httpclient/lib/clients/aps/dispensation';
import { dispensationApi } from '../../../httpclient';
import { CapitalizeString } from '../../../helpers/certificateHelpers';
import { routes } from '../../../constants/routes';
import { useNavigateWithLang } from '../../../services/hooks/useNavigateWithLang';

const SearchDispensationPage: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigateWithLang();
  const [searchError, setSearchError] = useState<any>(null);
  const [dispensation, setDispensation] = useState<DispensationDto>();
  const [loading, setLoading] = useState<boolean>(false);

  const initialValues = {
    dispensationNumber: '',
  };

  const handleSubmit = useCallback(
    (values) => {
      setLoading(true);
      dispensationApi
        .verifyDispensation(values?.dispensationNumber)
        .then((response) => {
          if (response.status === 200) {
            setDispensation(response.data);
            setSearchError(undefined);
          }
        })
        .catch((error) => {
          const { status } = error?.response || {};
          if (status >= 400 && status < 500) {
            setSearchError(intl.formatMessage({ id: 'dispensation.notfound' }));
          } else {
            setSearchError(error);
          }
          setDispensation(undefined);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [intl]
  );

  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      dispensationNumber: Yup.string().required(intl.formatMessage({ id: 'required.field' })),
    });
  }, [intl]);

  return (
    <>
      <BackLink
        text={intl.formatMessage({ id: 'link.back.title' })}
        onClick={() => navigate(routes.home)}
      />
      <h1>{intl.formatMessage({ id: 'dispensation.search.header' })}</h1>
      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {() => (
          <StyledForm>
            <Field
              label={intl.formatMessage({ id: 'dispensation.number' })}
              name="dispensationNumber"
              component={FormInput}
            />
            <StyledSdirButton
              htmlType="submit"
              type="primary"
              text={intl.formatMessage({ id: 'search' })}
            />
            {loading && <Loader />}
            {searchError && <ErrorMessage error={searchError} />}
          </StyledForm>
        )}
      </Formik>
      {dispensation && !searchError && (
        <ResultWrapper>
          {dispensation?.status === 'Cancelled' ? (
            <AlertSingleLine
              title=""
              text={intl.formatMessage({ id: 'dispensation.notvalid' })}
              type="info"
            />
          ) : (
            <div translate="no">
              <DispensationExtended
                dateOfBirth={dispensation?.seafarer?.dateOfBirth || ''}
                name={dispensation?.seafarer?.name || ''}
                issuedDate={dispensation?.issuedAt}
                certificateName={CapitalizeString(dispensation?.certificateName || '')}
                authorizedOfficial={dispensation?.authorizedOfficial}
                callsign={dispensation?.vessel?.callSign}
                dispensationNr={dispensation?.dispensationNumber}
                legalAuthority={dispensation?.legalAuthority}
                nationality={dispensation?.seafarer?.nationality || ''}
                occupationName={dispensation?.occupationTitle}
                remarks={dispensation?.remark || ''}
                validFrom={dispensation?.periodStart}
                validTo={dispensation?.periodEnd}
                vesselName={dispensation?.vessel?.name}
                hideQr
                qrImageLoading={false}
                // eslint-disable-next-line max-len
                qrData={`${process.env.REACT_APP_VERIFY_URL}/dispensation/${dispensation?.dispensationNumber}`}
                hideLanguageSelector
              />
            </div>
          )}
        </ResultWrapper>
      )}
    </>
  );
};

const StyledForm = styled(Form)`
  display: flex;
  grid-row-gap: 1em;
  flex-direction: column;
  max-width: 400px;
`;

const StyledSdirButton = styled(SdirButton)`
  width: fit-content;
`;

const ResultWrapper = styled.div`
  margin: 20px 0;

  p {
    margin: 0;
  }
`;

export default SearchDispensationPage;
